import axios from 'axios';
import { getAppPath, getDemoApiBaseUrl, getStorageItem, getStorageKey, isDemoMode, removeStorageItem, setStorageItem } from '../utils/runtimeMode';

const LOCAL_API_CACHE_KEY = getStorageKey('LAST_WORKING_API_BASE_URL');
let localApiResolutionPromise = null;
const LOCAL_BACKEND_PORTS = new Set(['3001', '3015']);
const REMOTE_DEV_FRONTEND_PORTS = new Set(['3000', '3002']);

const isLocalhostName = (hostname = '') => hostname === 'localhost' || hostname === '127.0.0.1';

const isIpv4Address = (hostname = '') => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);

const isLocalUrl = (url = '') => {
  try {
    const parsed = new URL(url);
    return isLocalhostName(parsed.hostname);
  } catch (e) {
    return false;
  }
};

const getLocalUrlPort = (url = '') => {
  try {
    const parsed = new URL(url);
    return String(parsed.port || (parsed.protocol === 'https:' ? '443' : '80'));
  } catch (e) {
    return '';
  }
};

const isAllowedLocalApiUrl = (url = '', allowedPorts = []) => {
  if (!isLocalUrl(url)) {
    return false;
  }

  return new Set(allowedPorts.map((port) => String(port))).has(getLocalUrlPort(url));
};

const getAllowedLocalApiPorts = (demoMode = isDemoMode()) => {
  if (demoMode) {
    return [String(process.env.REACT_APP_DEMO_BACKEND_PORT || '3015')];
  }

  const demoBackendPort = String(process.env.REACT_APP_DEMO_BACKEND_PORT || '3015');

  return Array.from(new Set([
    process.env.REACT_APP_BACKEND_PORT || '3001',
    '3001'
  ]
    .filter(Boolean)
    .map((port) => String(port))
    .filter((port) => port !== demoBackendPort)));
};

const isLocalDevFrontend = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }

  const { hostname, port } = window.location;
  return isLocalhostName(hostname) && !LOCAL_BACKEND_PORTS.has(String(port || ''));
};

const isRemoteIpDevFrontend = () => {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }

  const { hostname, port } = window.location;
  return isIpv4Address(hostname) && REMOTE_DEV_FRONTEND_PORTS.has(String(port || ''));
};

const getLocalApiCandidates = () => {
  if (typeof window === 'undefined' || !window.location) {
    return [];
  }

  const { protocol, hostname } = window.location;

  if (isDemoMode()) {
    const cachedCandidate = getCachedLocalApiUrl(getAllowedLocalApiPorts(true));

    return Array.from(new Set([
      getDemoApiBaseUrl(),
      cachedCandidate,
    ].filter(Boolean)));
  }

  const configuredBackendPort = process.env.REACT_APP_BACKEND_PORT;
  const allowedPorts = getAllowedLocalApiPorts(false);
  const cachedCandidate = getCachedLocalApiUrl(allowedPorts);
  const configuredCandidate = configuredBackendPort && allowedPorts.includes(String(configuredBackendPort))
    ? `${protocol}//${hostname}:${configuredBackendPort}`
    : null;

  return Array.from(new Set([
    configuredCandidate,
    `${protocol}//${hostname}:3001`,
    cachedCandidate,
  ].filter(Boolean)));
};

const getCachedLocalApiUrl = (allowedPorts = []) => {
  try {
    const cached = window.localStorage.getItem(LOCAL_API_CACHE_KEY);
    if (cached && isAllowedLocalApiUrl(cached, allowedPorts)) {
      return cached;
    }

    if (cached) {
      window.localStorage.removeItem(LOCAL_API_CACHE_KEY);
    }

    return null;
  } catch (e) {
    return null;
  }
};

const cacheWorkingLocalApiUrl = (url) => {
  try {
    window.localStorage.setItem(LOCAL_API_CACHE_KEY, url);
  } catch (e) {
    // ignore localStorage write failures
  }
};

const probeLocalApiUrl = async (baseUrl) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), 1500) : null;

  try {
    const response = await fetch(`${baseUrl}/api/login`, {
      method: 'GET',
      credentials: 'include',
      signal: controller?.signal,
    });

    return response.ok;
  } catch (error) {
    return false;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const resolveLocalApiBaseUrl = async () => {
  if (!isLocalDevFrontend()) {
    return null;
  }

  if (localApiResolutionPromise) {
    return localApiResolutionPromise;
  }

  localApiResolutionPromise = (async () => {
    const preferredCandidates = getLocalApiCandidates();
    const candidates = Array.from(new Set(preferredCandidates.filter(Boolean)));

    for (const candidate of candidates) {
      const isReachable = await probeLocalApiUrl(candidate);
      if (isReachable) {
        cacheWorkingLocalApiUrl(candidate);
        console.log('API service auto-detected local backend:', candidate);
        return candidate;
      }
    }

    return preferredCandidates[0] || null;
  })();

  return localApiResolutionPromise;
};

// Choose API base URL at runtime so the same build works when served by the backend
// or when running the React dev server.
const getApiUrl = () => {
  const runningLocally = typeof window !== 'undefined' && window.location && isLocalhostName(window.location.hostname);
  const demoMode = isDemoMode();

  // 0) Runtime override via localStorage (useful to flip between prod/local without restart)
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const runtime = getStorageItem('API_BASE_URL');
      if (runtime && runtime.trim() && (!runningLocally || isAllowedLocalApiUrl(runtime, getAllowedLocalApiPorts(demoMode)))) {
        console.log('API service using runtime override from localStorage ->', runtime);
        return runtime;
      }

      if (runtime && runningLocally && !isAllowedLocalApiUrl(runtime, getAllowedLocalApiPorts(demoMode))) {
        removeStorageItem('API_BASE_URL');
      }
    }
  } catch (e) {
    // ignore localStorage access errors
  }

  if (demoMode) {
    return getDemoApiBaseUrl();
  }

  // 1) Explicit override usable in env: REACT_APP_API_URL
  if (process.env.REACT_APP_API_URL && (!runningLocally || isLocalUrl(process.env.REACT_APP_API_URL))) {
    return process.env.REACT_APP_API_URL;
  }

  // 2) If running in a browser, prefer same-origin (useful when backend serves the build)
  if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname, port } = window.location;
    const backendPort = process.env.REACT_APP_BACKEND_PORT || 3001;

    // If we appear to be on the production host, use the production API
    if (hostname && hostname.includes('clocktower.3p3.ca')) {
      return 'https://clocktower.3p3.ca';
    }

    // When running locally from any non-backend localhost port (e.g. 3000 or 3002),
    // use the preferred local backend candidate instead of same-origin.
    if (isLocalDevFrontend()) {
      return getLocalApiCandidates()[0] || `${protocol}//${hostname}:${backendPort}`;
    }

    // When the React dev server is opened directly on an EC2 public/private IPv4
    // (for example http://40.x.x.x:3000), API calls should target the backend port,
    // not the frontend dev server port.
    if (isRemoteIpDevFrontend()) {
      return `${protocol}//${hostname}:${backendPort}`;
    }

    // If page is already served from a port (e.g. backend at 3001), use same origin
    if (port) {
      return `${protocol}//${hostname}:${port}`;
    }

    // Otherwise fall back to REACT_APP_BACKEND_PORT or default 3001
    return `${protocol}//${hostname}:${backendPort}`;
  }

  // 3) Fallback to production
  return 'https://clocktower.3p3.ca';
};

const API_URL = getApiUrl();
console.log('API service using base URL:', API_URL);

// Create axios instance with proper configuration
const API = axios.create({
  baseURL: API_URL, // No port! Use the ALB
  withCredentials: true,
  // Snowflake-backed endpoints can be slow on cold starts; 10s was too aggressive and
  // caused first-load failures on investigation dashboards.
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i;

export const getApiBaseUrl = () => String(API.defaults.baseURL || API_URL || '').replace(/\/+$/, '');

export const getApiResourceUrl = (path = '') => {
  const value = String(path || '').trim();
  if (!value) {
    return getApiBaseUrl();
  }

  if (ABSOLUTE_URL_PATTERN.test(value)) {
    return value;
  }

  const normalizedPath = value.startsWith('/') ? value : `/${value}`;
  return `${getApiBaseUrl()}${normalizedPath}`;
};

// Add request interceptor to always include auth token if available
API.interceptors.request.use(
  async config => {
    if (isLocalDevFrontend()) {
      const resolvedBaseUrl = await resolveLocalApiBaseUrl();
      if (resolvedBaseUrl) {
        config.baseURL = resolvedBaseUrl;
        API.defaults.baseURL = resolvedBaseUrl;
      }
    }

    const token = getStorageItem('authToken');
    if (token) {
      // Try both header formats to ensure compatibility
      config.headers.authorization = `Bearer ${token}`;
      config.headers['x-auth-token'] = token;
    }
    
    // If the request is sending FormData, remove any default Content-Type so
    // the browser/axios can set the correct multipart boundary. This prevents
    // multer on the server from receiving an incorrectly labeled request which
    // results in `req.file` being undefined and a 400 response ('No file uploaded').
    if (config && config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
      try {
        if (config.headers && config.headers['Content-Type']) {
          delete config.headers['Content-Type'];
        }
      } catch (e) {
        // ignore
      }
    }

    // Add withCredentials for cookies
    config.withCredentials = true;
    
    return config;
  },
  error => Promise.reject(error)
);

// Add this debugging interceptor to verify token is being sent:
API.interceptors.request.use(config => {
  const token = getStorageItem('authToken');
  console.log('🔑 API Request to:', config.url);
  console.log('🔑 Auth token present:', !!token);
  if (token && token.length > 20) {
    console.log('🔑 Token format looks valid (first 10 chars):', token.substring(0, 10) + '...');
  }
  return config;
});

// Add this interceptor for debugging
API.interceptors.request.use(request => {
  if (request.url.includes('/api/hazards/history')) {
    console.log('🔍 Request headers for hazards history:', request.headers);
    console.log('🔍 Auth header present:', !!request.headers.authorization);
    console.log('🔍 Auth header value:', request.headers.authorization);
  }
  return request;
});

// Add response interceptor for better error handling
API.interceptors.response.use(
  response => response,
  async error => {
    // If error is 401 and we have a token, it might be expired
    if (error.response?.status === 401 && getStorageItem('authToken')) {
      console.log('📢 Got 401 with existing token - attempting refresh');
      try {
        // Try to refresh the token
        const response = await axios.post(`${API.defaults.baseURL || API_URL}/api/auth/refresh-token`, {}, {
          withCredentials: true
        });
        
        if (response.data?.token) {
          setStorageItem('authToken', response.data.token);
          API.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          
          // Retry the original request
          return API(error.config);
        }
      } catch (refreshError) {
        console.log('Token refresh failed:', refreshError);
        // Force logout on refresh failure
        removeStorageItem('authToken');
        window.location.href = getAppPath('/login');
      }
    }
    
    return Promise.reject(error);
  }
);

export default API;